import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaArrowLeft, FaNewspaper, FaFlask, FaQuestionCircle } from 'react-icons/fa';
import logo from '../../assets/logo.png';
import BrandName from '../BrandName';

const GLP1Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const sectionLinks = [
    { name: 'How It Works', href: '/#how-it-works', isSection: true },
    { name: 'Pricing', href: '/#pricing', isSection: true },
    { name: 'Doctors', href: '/#doctors', isSection: true },
  ];

  const scrollToSection = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      // If we're not on the home page, navigate there first
      if (location.pathname !== '/') {
        window.location.href = href;
        return;
      }
      const sectionId = href.replace('/#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-navy-900 text-white py-2 px-4 text-center text-sm">
        <span className="hidden sm:inline">India's First Doctor-Supervised GLP-1 Program | </span>
        <Link to="/eligibility" className="text-gold-400 hover:text-gold-300 font-medium">
          Check Your Eligibility →
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left: Back Link + Logo */}
            <div className="flex items-center space-x-4">
              <a
                href="https://fitnession.fit"
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center"
              >
                <FaArrowLeft className="mr-1 text-xs" />
                <span className="hidden sm:inline">Back to Fitnession</span>
              </a>
              <Link to="/" className="flex items-center gap-2">
                <img src={logo} alt="Fitnession" className="h-10 w-auto" />
                <BrandName className="text-xl" />
                <span className="text-gold-500 text-sm font-semibold">GLP-1</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {sectionLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-gray-700 hover:text-navy-900 font-medium transition-colors text-sm"
                >
                  {link.name}
                </a>
              ))}
              <Link
                to="/research"
                className={`flex items-center gap-1 font-medium transition-colors text-sm ${
                  location.pathname === '/research'
                    ? 'text-gold-600 border-b-2 border-gold-500 pb-0.5'
                    : 'text-gray-700 hover:text-navy-900'
                }`}
              >
                <FaFlask className="text-xs" />
                Research
              </Link>
              <Link
                to="/faq"
                className={`flex items-center gap-1 font-medium transition-colors text-sm ${
                  location.pathname === '/faq'
                    ? 'text-gold-600 border-b-2 border-gold-500 pb-0.5'
                    : 'text-gray-700 hover:text-navy-900'
                }`}
              >
                <FaQuestionCircle className="text-xs" />
                FAQs
              </Link>
              <Link
                to="/blog"
                className={`flex items-center gap-1 font-medium transition-colors text-sm ${
                  location.pathname.startsWith('/blog')
                    ? 'text-gold-600 border-b-2 border-gold-500 pb-0.5'
                    : 'text-gray-700 hover:text-navy-900'
                }`}
              >
                <FaNewspaper className="text-xs" />
                Blog
              </Link>
            </div>

            {/* Right: Phone + CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <a href="tel:+919579520586" className="flex items-center text-gray-600 hover:text-navy-900">
                <FaPhone className="mr-2 text-sm" />
                <span className="text-sm">+91 9579520586</span>
              </a>
              <Link
                to="/eligibility"
                className="bg-gold-500 hover:bg-gold-600 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors shadow-md text-sm"
              >
                Check Eligibility
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-navy-900"
            >
              {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              {sectionLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block py-2 text-gray-700 hover:text-navy-900 font-medium"
                >
                  {link.name}
                </a>
              ))}
              <Link
                to="/research"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-2 py-2 font-medium ${
                  location.pathname === '/research' ? 'text-gold-600' : 'text-gray-700 hover:text-navy-900'
                }`}
              >
                <FaFlask className="text-sm" />
                Research
              </Link>
              <Link
                to="/faq"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-2 py-2 font-medium ${
                  location.pathname === '/faq' ? 'text-gold-600' : 'text-gray-700 hover:text-navy-900'
                }`}
              >
                <FaQuestionCircle className="text-sm" />
                FAQs
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-2 py-2 font-medium ${
                  location.pathname.startsWith('/blog') ? 'text-gold-600' : 'text-gray-700 hover:text-navy-900'
                }`}
              >
                <FaNewspaper className="text-sm" />
                Blog
              </Link>
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <a
                  href="tel:+919579520586"
                  className="flex items-center text-gray-600 py-2"
                >
                  <FaPhone className="mr-2" />
                  +91 9579520586
                </a>
                <Link
                  to="/eligibility"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-center bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  Check Eligibility
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default GLP1Navbar;
